'use client';

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrentUser, SidebarIcon } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import type { PaddingBox } from '../_shared/resolvePadding';
import { SectionShell } from '../_shared/SectionShell';
import { useMounted } from '../_shared/useMounted';

// ─── BFF response shapes ──────────────────────────────────────────────────────

interface SummaryResponse {
  score?: number;
  totalPoints?: number;
  rank?: number;
  pointName?: string;
  levelName?: string;
  levelCode?: string;
  levelPercent?: number;
  badgeCount?: number;
  streakDays?: number;
  pointsToNextLevel?: number;
  nextLevelName?: string;
  progressPercent?: number;
}

interface BadgeResponse {
  id?: string | number;
  elementId?: string;
  name?: string;
  iconUrl?: string;
  total?: number;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface GamificationPanelV2Props {
  /** Author-supplied copy overrides (labels / locale). Data always comes from API. */
  heading?: string;
  streakLabel?: string;
  rankLabel?: string;
  progressTitle?: string;
  pointsRemainingLabel?: string;
  badgesTitle?: string;

  requireAuth?: 'any' | 'in' | 'out';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

// ─── Config ─────────────────────────────────────────────────────────────────

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
const bffUrl = (path: string) => `${BASE}${path}`;

// ─── Component ────────────────────────────────────────────────────────────────

export function GamificationPanelV2({
  heading,
  streakLabel,
  rankLabel,
  progressTitle,
  pointsRemainingLabel,
  badgesTitle,
  requireAuth,
  paddingOuter,
  paddingInner,
  style,
}: GamificationPanelV2Props) {
  const { t } = useTranslation('site-builder-blocks');
  const headingText = heading ?? t('gamification.panelV2.heading');
  const streakText = streakLabel ?? t('gamification.panelV2.streak');
  const rankText = rankLabel ?? t('gamification.panel.rank');
  const progressText = progressTitle ?? t('gamification.panel.progressTitle');
  const pointsRemainingText = pointsRemainingLabel ?? t('gamification.panel.pointsRemaining');
  const badgesText = badgesTitle ?? t('gamification.panelV2.badgesTitle');
  const currentUser = useCurrentUser<{
    name?: string;
    surname?: string;
    initials?: string;
    avatar?: string;
  }>();
  const cls = `lms-gamif2-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const mounted = useMounted();

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [badges, setBadges] = useState<BadgeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const abort = useRef<AbortController | null>(null);

  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';

  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false);
      return;
    }

    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setLoading(true);

    Promise.all([
      fetch(bffUrl('/api/gamification/summary'), { signal: ctrl.signal })
        .then<SummaryResponse | null>((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch(bffUrl('/api/gamification/all-badges'), { signal: ctrl.signal })
        .then<BadgeResponse[]>((r) => (r.ok ? r.json() : []))
        .catch(() => []),
    ]).then(([sum, bdg]) => {
      if (ctrl.signal.aborted) return;
      setSummary(sum);
      setBadges(Array.isArray(bdg) ? bdg : []);
      setLoading(false);
    });

    return () => ctrl.abort();
  }, [isSignedIn]);

  // Hold until mounted so the auth-gated / user-dependent render can't diverge
  // between the server (no redux-persist user) and the first client render.
  if (!mounted) return null;
  if ((gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn)) return null;

  // ── Derived display values ────────────────────────────────────────────────

  const score = summary?.score ?? summary?.totalPoints;
  const levelName = summary?.levelName;
  const bar = clamp(summary?.progressPercent ?? 0);
  const streak = summary?.streakDays ?? 0;
  const rankNum = summary?.rank ?? 0;
  const pointsLeft = summary?.pointsToNextLevel;

  // Certificates intentionally omitted from v2 — pure gamification metrics only.
  // Points + badges are the headline totals (grouped top-right); rank + streak
  // become colour-coded pills (modern candy palette).
  type Stat = { icon: string; value: string; label: string; tone: string };
  const headStats = [
    score != null && {
      icon: 'zap',
      value: score.toLocaleString(),
      label: summary?.pointName ?? t('gamification.panel.points'),
      tone: 'gold',
    },
    summary?.badgeCount != null && {
      icon: 'star',
      value: String(summary.badgeCount),
      label: t('gamification.panel.badge'),
      tone: 'violet',
    },
  ].filter(Boolean) as Stat[];
  const pills = [
    rankNum > 0 && { icon: 'trophy', value: `#${rankNum}`, label: rankText, tone: 'teal' },
    streak > 0 && { icon: 'flame', value: String(streak), label: streakText, tone: 'rose' },
  ].filter(Boolean) as Stat[];

  // earned first, locked last — max 12 shown in the showcase
  const badgeRow = [...badges].sort((a, b) => (b.total ?? 0) - (a.total ?? 0)).slice(0, 12);
  const earnedCount = badges.filter((b) => (b.total ?? 0) > 0).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner} style={style}>
      <section className={`${cls}-root`}>
        <style dangerouslySetInnerHTML={{ __html: css(cls, bar) }} />

        {loading ? (
          <Skeleton cls={cls} />
        ) : (
          <Card asChild>
            <div className={`${cls}-card`}>
              {/* ── Header: identity + Puan & Rozet totals (grouped) ──────── */}
              <div className={`${cls}-head`}>
                <div className={`${cls}-id`}>
                  <div className={`${cls}-ring`}>
                    <div className={`${cls}-ringInner`}>
                      {currentUser?.avatar ? (
                        <img
                          src={currentUser.avatar}
                          alt={currentUser.name ?? ''}
                          className={`${cls}-avatarImg`}
                        />
                      ) : (
                        <span className={`${cls}-avatarInit`}>
                          {currentUser?.initials ??
                            currentUser?.name?.slice(0, 2).toUpperCase() ??
                            '?'}
                        </span>
                      )}
                    </div>
                    {summary?.levelCode && <b className={`${cls}-lvlChip`}>{summary.levelCode}</b>}
                  </div>
                  <div className={`${cls}-idText`}>
                    <div className={`${cls}-eyebrow`}>{levelName ?? headingText}</div>
                    <div className={`${cls}-name`}>
                      {currentUser?.name} {currentUser?.surname}
                    </div>
                  </div>
                </div>

                {headStats.length > 0 && (
                  <div className={`${cls}-headStats`}>
                    {headStats.map((stat, i) => (
                      <div key={i} className={`${cls}-hstat tone-${stat.tone}`}>
                        <span className={`${cls}-hstatIcon`}>
                          <SidebarIcon name={stat.icon} size={16} />
                        </span>
                        <div>
                          <div className={`${cls}-hstatNum`}>{stat.value}</div>
                          <div className={`${cls}-hstatLbl`}>{stat.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Colourful metric pills (rank + streak) ────────────────── */}
              {pills.length > 0 && (
                <div className={`${cls}-pills`}>
                  {pills.map((pill, i) => (
                    <div key={i} className={`${cls}-pill tone-${pill.tone}`}>
                      <span className={`${cls}-pillIcon`}>
                        <SidebarIcon name={pill.icon} size={15} />
                      </span>
                      <b className={`${cls}-pillVal`}>{pill.value}</b>
                      <span className={`${cls}-pillLbl`}>{pill.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Level-up progress ─────────────────────────────────────── */}
              <div className={`${cls}-prog`}>
                <div className={`${cls}-progHead`}>
                  <span className={`${cls}-eyebrow`}>{progressText}</span>
                  {pointsLeft != null ? (
                    <span className={`${cls}-progRemain`}>
                      <b>{pointsLeft.toLocaleString()}</b> {pointsRemainingText}
                    </span>
                  ) : (
                    summary?.nextLevelName && (
                      <span className={`${cls}-progRemain`}>
                        <Trans
                          t={t}
                          i18nKey="gamification.panelV2.nextLevel"
                          values={{ level: summary.nextLevelName }}
                          components={{ b: <b /> }}
                        />
                      </span>
                    )
                  )}
                </div>
                <div className={`${cls}-bar`}>
                  <i />
                  <em>{bar}%</em>
                </div>
              </div>

              {/* ── Badges strip ──────────────────────────────────────────── */}
              {badgeRow.length > 0 && (
                <div className={`${cls}-badges`}>
                  <div className={`${cls}-badgesHead`}>
                    <span className={`${cls}-eyebrow`}>{badgesText}</span>
                    <span className={`${cls}-badgesCount`}>
                      {earnedCount}/{badges.length}
                    </span>
                  </div>
                  <div className={`${cls}-badgeGrid`}>
                    {badgeRow.map((b, i) => {
                      const earned = (b.total ?? 0) > 0;
                      return (
                        <div
                          key={b.id ?? i}
                          className={`${cls}-badge${earned ? '' : ' locked'}`}
                          title={b.name}
                        >
                          <span className={`${cls}-badgeIco`}>
                            {b.iconUrl ? (
                              <img src={b.iconUrl} alt={b.name ?? ''} />
                            ) : (
                              <SidebarIcon name="award" size={18} />
                            )}
                            {(b.total ?? 0) > 1 && <b>{b.total}</b>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </section>
    </SectionShell>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(n: number): number {
  return Math.max(0, Math.min(100, Math.round(n)));
}

function css(cls: string, bar: number): string {
  // ── Palette — brand-native, token-driven ─────────────────────────────
  // One accent: Akademi teal. Everything else is neutral surface/border/text
  // tokens, so the block matches the rest of the product and adapts to the
  // portal's own light/dark theme automatically. No hardcoded page colours.
  // ── Modern "candy" palette — colourful but tasteful ──────────────────
  const AMBER = '#f59e0b';
  const AMBER_L = '#fbbf24';
  const VIOLET = '#8b5cf6';
  const VIOLET_D = '#7c3aed';
  const TEAL = '#06b6d4';
  const TEAL_D = '#0891b2';
  const ROSE = '#fb7185';
  const ROSE_D = '#f43f5e';
  // Surface/text via tokens so it adapts to the portal's light/dark theme.
  const INK = 'var(--color-text-primary, #101828)';
  const MUT = 'var(--color-text-secondary, #667085)';
  const CARD = 'var(--color-surface-card, #ffffff)';
  const FILL = 'var(--color-surface-muted, #f4f5f7)';
  const BORDER = 'var(--color-border-subtle, #eaecf0)';
  const GRAD = `${VIOLET}, ${TEAL} 55%, ${AMBER_L}`;

  return `
.${cls}-root{width:100%;margin:0}

/* ── One full-width card ─────────────────────────────────────────────── */
/* Rendered as <Card>: border + elevation come from the component. */
.${cls}-card{position:relative;box-sizing:border-box;border-radius:20px;padding:16px 20px;
  display:flex;flex-direction:column;gap:14px;
  background:${CARD}}
.${cls}-eyebrow{font-size:9.5px;text-transform:uppercase;letter-spacing:.08em;font-weight:700;color:${MUT}}

/* ── Header ──────────────────────────────────────────────────────────── */
.${cls}-head{display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap}
.${cls}-id{display:flex;align-items:center;gap:13px;min-width:0}
.${cls}-ring{position:relative;width:52px;height:52px;border-radius:999px;flex-shrink:0;padding:2.5px;
  background:conic-gradient(from -90deg, ${VIOLET}, ${TEAL} 55%, ${AMBER_L} ${bar}%, ${BORDER} 0)}
.${cls}-ringInner{width:100%;height:100%;border-radius:999px;overflow:hidden;display:grid;place-items:center;
  background:linear-gradient(150deg, ${VIOLET}, ${TEAL});border:2.5px solid ${CARD}}
.${cls}-avatarImg{width:100%;height:100%;object-fit:cover}
.${cls}-avatarInit{color:#fff;font-weight:800;font-size:18px;line-height:1}
.${cls}-lvlChip{position:absolute;bottom:-5px;left:50%;transform:translateX(-50%);
  background:linear-gradient(135deg, ${AMBER_L}, ${AMBER});color:#3a2606;font-size:9px;font-weight:900;
  padding:1.5px 7px;border-radius:999px;font-style:normal;white-space:nowrap;box-shadow:0 0 0 2px ${CARD}}
.${cls}-idText{min-width:0}
.${cls}-name{font-size:16px;font-weight:800;letter-spacing:-.01em;margin-top:2px;color:${INK};
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

/* Header totals — Puan & Rozet chips grouped top-right */
.${cls}-headStats{display:flex;align-items:center;gap:8px;flex-shrink:0}
.${cls}-hstat{display:flex;align-items:center;gap:9px;padding:6px 12px 6px 6px;border-radius:14px;
  background:var(--ht);box-shadow:inset 0 0 0 1px var(--hb)}
.${cls}-hstatIcon{width:32px;height:32px;border-radius:10px;display:grid;place-items:center;color:#fff;flex-shrink:0;
  background:linear-gradient(140deg, var(--h1), var(--h2));box-shadow:0 4px 10px -4px var(--h2)}
.${cls}-hstatNum{font-size:22px;font-weight:900;line-height:1;letter-spacing:-.02em;color:var(--hn);
  font-variant-numeric:tabular-nums}
.${cls}-hstatLbl{font-size:9px;text-transform:uppercase;letter-spacing:.09em;font-weight:800;color:${MUT};margin-top:3px}
.${cls}-hstat.tone-gold{--h1:${AMBER_L};--h2:${AMBER};--hn:${AMBER};
  --ht:linear-gradient(135deg, rgba(251,191,36,.16), rgba(245,158,11,.12));--hb:rgba(245,158,11,.3)}
.${cls}-hstat.tone-violet{--h1:${VIOLET};--h2:${VIOLET_D};--hn:${VIOLET};
  --ht:rgba(139,92,246,.12);--hb:rgba(139,92,246,.26)}

/* ── Colourful metric pills ──────────────────────────────────────────── */
.${cls}-pills{display:flex;flex-wrap:wrap;gap:8px}
.${cls}-pill{flex:1 1 auto;display:flex;align-items:center;gap:9px;padding:8px 12px 8px 8px;border-radius:13px;
  background:var(--pt);box-shadow:inset 0 0 0 1px var(--pb)}
.${cls}-pillIcon{width:28px;height:28px;border-radius:9px;display:grid;place-items:center;color:#fff;flex-shrink:0;
  background:linear-gradient(140deg, var(--p1), var(--p2));box-shadow:0 4px 9px -4px var(--p2)}
.${cls}-pillVal{font-size:17px;font-weight:900;color:${INK};letter-spacing:-.02em;font-variant-numeric:tabular-nums;font-style:normal}
.${cls}-pillLbl{font-size:10px;text-transform:uppercase;letter-spacing:.05em;font-weight:700;color:${MUT}}
.${cls}-pill.tone-violet{--p1:${VIOLET};--p2:${VIOLET_D};--pt:rgba(139,92,246,.10);--pb:rgba(139,92,246,.22)}
.${cls}-pill.tone-teal{--p1:${TEAL};--p2:${TEAL_D};--pt:rgba(6,182,212,.10);--pb:rgba(6,182,212,.22)}
.${cls}-pill.tone-rose{--p1:${ROSE};--p2:${ROSE_D};--pt:rgba(251,113,133,.10);--pb:rgba(251,113,133,.22)}

/* ── Progress ────────────────────────────────────────────────────────── */
.${cls}-progHead{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
.${cls}-progRemain{font-size:11.5px;color:${MUT}}
.${cls}-progRemain b{font-weight:800;color:${VIOLET_D}}
.${cls}-bar{position:relative;height:12px;border-radius:999px;overflow:hidden;background:${FILL};
  box-shadow:inset 0 0 0 1px ${BORDER}}
.${cls}-bar>i{position:relative;display:block;height:100%;width:${bar}%;border-radius:999px;overflow:hidden;
  background:linear-gradient(90deg, ${GRAD});box-shadow:0 0 12px -2px ${VIOLET}}
.${cls}-bar>i::after{content:"";position:absolute;inset:0;
  background:linear-gradient(100deg, transparent 20%, rgba(255,255,255,.5) 50%, transparent 80%);
  background-size:200% 100%;animation:${cls}-shine 2.6s linear infinite}
.${cls}-bar>em{position:absolute;top:50%;right:8px;transform:translateY(-50%);font-style:normal;font-size:8.5px;
  font-weight:900;color:#fff;text-shadow:0 1px 2px rgba(16,24,40,.45)}
@keyframes ${cls}-shine{0%{background-position:200% 0}100%{background-position:-200% 0}}
@media (prefers-reduced-motion:reduce){.${cls}-bar>i::after{animation:none}}

/* ── Badges strip ────────────────────────────────────────────────────── */
.${cls}-badgesHead{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
.${cls}-badgesCount{font-size:10px;font-weight:800;color:#fff;padding:2px 9px;border-radius:999px;
  background:linear-gradient(135deg, ${VIOLET}, ${VIOLET_D})}
.${cls}-badgeGrid{display:flex;flex-wrap:wrap;gap:9px}
.${cls}-badge{flex-shrink:0}
.${cls}-badgeIco{position:relative;width:40px;height:40px;border-radius:12px;display:grid;place-items:center;color:#fff;
  background:linear-gradient(150deg, ${AMBER_L}, ${AMBER});box-shadow:0 5px 12px -6px ${AMBER};transition:transform .16s ease}
.${cls}-badge:nth-child(3n+2) .${cls}-badgeIco{background:linear-gradient(150deg, ${VIOLET}, ${VIOLET_D});box-shadow:0 5px 12px -6px ${VIOLET_D}}
.${cls}-badge:nth-child(3n) .${cls}-badgeIco{background:linear-gradient(150deg, ${TEAL}, ${TEAL_D});box-shadow:0 5px 12px -6px ${TEAL_D}}
.${cls}-badge:hover .${cls}-badgeIco{transform:translateY(-2px) scale(1.05)}
.${cls}-badgeIco img{width:100%;height:100%;object-fit:contain;padding:8px}
.${cls}-badge.locked .${cls}-badgeIco{background:${FILL}!important;color:${MUT};box-shadow:none!important;border:1px dashed ${BORDER}}
.${cls}-badge.locked .${cls}-badgeIco img{filter:grayscale(1);opacity:.4}
.${cls}-badgeIco b{position:absolute;top:-5px;right:-5px;background:#fff;color:${INK};font-size:9px;font-weight:900;
  min-width:16px;height:16px;padding:0 3px;border-radius:999px;display:grid;place-items:center;font-style:normal;
  box-shadow:0 1px 4px rgba(16,24,40,.25)}

/* ── Skeleton ────────────────────────────────────────────────────────── */
@keyframes ${cls}-pulse{0%,100%{opacity:1}50%{opacity:.5}}
.${cls}-skel{border-radius:12px;background:${FILL};animation:${cls}-pulse 1.5s ease-in-out infinite}
`;
}

function Skeleton({ cls }: { cls: string }) {
  return (
    <Card asChild>
      <div className={`${cls}-card`}>
        <div className={`${cls}-head`}>
          <div className={`${cls}-id`}>
            <div className={`${cls}-skel`} style={{ width: 52, height: 52, borderRadius: 999 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className={`${cls}-skel`} style={{ width: 70, height: 9 }} />
              <div className={`${cls}-skel`} style={{ width: 120, height: 15 }} />
            </div>
          </div>
          <div className={`${cls}-skel`} style={{ width: 96, height: 44, borderRadius: 14 }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`${cls}-skel`}
              style={{ flex: 1, height: 44, borderRadius: 13 }}
            />
          ))}
        </div>
        <div className={`${cls}-skel`} style={{ height: 12, borderRadius: 999 }} />
        <div className={`${cls}-skel`} style={{ height: 40, borderRadius: 12 }} />
      </div>
    </Card>
  );
}
